/* eslint-disable max-lines */
/* eslint-disable max-len */
import {
    Box,
    Button,
    HStack,
    MenuItem,
    Spacer,
    Text,
    VStack,
    FormControl,
    Link,
    FormLabel,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
} from '@chakra-ui/react';
import { ReportedFindingDataMachine } from '@acp/shared';
import {
    DatePickerField,
    FaIcon,
    RestrictedLengthTextAreaField,
} from '@acp/core-ui';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { ReportedFindingsMachineContext } from '../machine/context';
import {
    ReportedFindingsWizardRow,
    ReportedFindingsWizardRowHeader,
} from './table/reported-findings-wizard-row';
import { ToggleTooltipFallback } from './table/request-quote-wizard-row';
import { ReportedFindingsWizardSkeleton } from './table/reported-findings-wizard-table';

const builtYearFormSchema = z.object({
    selectedProblemRegisterIds: z.array(z.number()),
    propertyBuilt: z.date({
        required_error: 'Property build year is required',
    }),
    notes: z
        .string()
        .trim()
        .max(500, 'Message should be 500 characters or less')
        .optional(),
});

function EnterBuiltYearButton() {
    const machine = ReportedFindingsMachineContext.useActorRef();
    return (
        <MenuItem
            onClick={() => machine.send({
                type: 'ENTER_PROPERTY_BUILT_YEAR',
            })}
        >
            Enter property built year
        </MenuItem>
    );
}

function TooltipAction({
    title,
    children,
}: {
    title: string;
    children?: React.ReactNode;
}) {
    return (
        <Popover isLazy trigger="hover" placement="auto">
            <PopoverTrigger>
                <HStack mt={1}>
                    <Text color="muted.500" fontWeight="bold" fontSize="sm">
                        {title}
                    </Text>
                    <FaIcon icon={faCheckCircle} color="muted.500" ml={1} />
                </HStack>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverBody>
                    <VStack alignItems="flex-start" gap={0}>
                        {children}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

function BuiltYearAction({
    canEnterBuiltYear = false,
}: {
    canEnterBuiltYear: boolean;
}) {
    const showBuiltYearInput = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.showBuiltYearInput
    );

    if (showBuiltYearInput && canEnterBuiltYear) {
        return (
            <TooltipAction title="To be resolved">
                <Text fontSize="small">
                    This problem will be resolved by the build date you have entered.
                </Text>
            </TooltipAction>
        );
    }

    return (
        <ToggleTooltipFallback title="Inapplicable">
            <Text fontSize="small">
                Detector Inspector does not require additional information.
            </Text>
        </ToggleTooltipFallback>
    );
}

function BuiltYearSelectItemRow({
    reportedFinding,
}: {
    reportedFinding: ReportedFindingDataMachine;
}) {
    const { canEnterBuiltYear } = reportedFinding.problemRegister;

    return (
        <ReportedFindingsWizardRow reportedFinding={reportedFinding}>
            <BuiltYearAction canEnterBuiltYear={canEnterBuiltYear} />
        </ReportedFindingsWizardRow>
    );
}

function BuiltYearSelectItems() {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const reportedFindings = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.reportedFindings
    );
    const builtYearForm = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.builtYearForm
    );
    const isLoading = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches({ enterBuiltYear: 'enterBuiltYearRequest' })
    );

    const { handleSubmit, control } = useForm<z.infer<typeof builtYearFormSchema>>({
        resolver: zodResolver(builtYearFormSchema),
        defaultValues: {
            selectedProblemRegisterIds: [],
            notes: builtYearForm.notes,
            propertyBuilt: builtYearForm.propertyBuilt,
        },
        mode: 'onChange',
    });

    const onSubmit = (vals: z.infer<typeof builtYearFormSchema>) => {
        machine.send({
            type: 'ENTER_BUILT_YEAR_CONFIRM',
            builtYearForm: {
                propertyBuilt: vals.propertyBuilt,
                notes: vals.notes ?? '',
            },
        });
    };

    return (
        <VStack align="left" w="100%" gap={0} mb={4}>
            <DatePickerField
                name="propertyBuilt"
                control={control}
                label="Property built year"
                isRequired
                allowClear={false}
                toDate={new Date()}
                dateDisplayFormat="MMMM yyyy"
                showMonthPicker
            />
            <VStack mt={4}>
                <Controller
                    control={control}
                    name="notes"
                    render={({ fieldState }) => (
                        <FormControl isInvalid={fieldState.invalid}>
                            <FormLabel>Provide information to DI</FormLabel>
                            <RestrictedLengthTextAreaField
                                control={control}
                                name="notes"
                                label=""
                                maxLength={500}
                            />
                        </FormControl>
                    )}
                />
            </VStack>
            <Text fontSize="md" mt={2}>
                The following problems will be resolved by the build date you have entered:
            </Text>
            <VStack alignItems="flex-start" spacing={4} mt={8}>
                <Box w="full">
                    <ReportedFindingsWizardRowHeader />
                </Box>

                {reportedFindings == null && <ReportedFindingsWizardSkeleton />}
                <VStack spacing={4} w="full">
                    {reportedFindings?.map((item) => (
                        <BuiltYearSelectItemRow
                            key={`${item.problemRegister.problemRegisterId}`}
                            reportedFinding={item}
                        />
                    ))}
                </VStack>
            </VStack>
            <HStack justifyContent="end" pt={6}>
                <Button
                    isDisabled={isLoading}
                    onClick={() => machine.send({ type: 'BACK' })}
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                >
                    Back
                </Button>
                <Spacer />
                <Button
                    isLoading={isLoading}
                    colorScheme="secondary"
                    size="md"
                    variant="solid"
                    onClick={handleSubmit(onSubmit)}
                    isDisabled={isLoading}
                >
                    <Text>Provide build year</Text>
                </Button>
            </HStack>
        </VStack>
    );
}

function BuiltYearCompleted() {
    const machine = ReportedFindingsMachineContext.useActorRef();

    return (
        <VStack align="left" w="100%" spacing={4} mt={4}>
            <VStack w="100%" alignItems="center" gap={4} pb={10}>
                <FaIcon w={12} h={12} icon={faCheckCircle} color="success.500" />
                <Text fontSize="xl" fontWeight="medium">
                    Thanks for the additional information
                </Text>
                <Box textAlign="center" mb={4} mt={2}>
                    <Text fontSize="md" color="muted.700">
                        Great work! You&apos;re on a roll.
                    </Text>
                    <Text fontSize="md" color="muted.700">
                        Want to resolve some more?
                    </Text>
                </Box>
                <Button
                    colorScheme="black"
                    size="md"
                    variant="solid"
                    onClick={() => machine.send({ type: 'RESOLVE_MORE' })}
                >
                    <Text>Continue</Text>
                </Button>

                {/* eslint-disable jsx-a11y/anchor-is-valid */}
                <Link
                    _hover={{ textDecoration: 'underline' }}
                    fontWeight="normal"
                    fontSize="md"
                    color="muted.500"
                    mt={4}
                    onClick={() => machine.send({ type: 'FORCE_CLOSE_MODAL' })}
                >
                    <Text color="muted.700">No thanks, I&apos;m finished</Text>
                </Link>
            </VStack>
        </VStack>
    );
}

export {
    BuiltYearSelectItems, BuiltYearCompleted, EnterBuiltYearButton,
};
